import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Banknote, TrendingUp, Wallet, Users, Loader2, Percent } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import { AdminSectionSkeleton } from "@/components/admin/AdminSkeletons";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;

export const FinanceModule = () => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [rate, setRate] = useState("");
  const [trial, setTrial] = useState("");
  const [saving, setSaving] = useState(false);
  const [ngZones, setNgZones] = useState([]);
  const [savingNg, setSavingNg] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/finance/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/finance/settings").then((r) => { setSettings(r.data); setRate(String(r.data.commission_pct)); setTrial(String(r.data.trial_months)); }).catch(() => {});
    api.get("/admin/nigeria-zones").then((r) => setNgZones(r.data.zones || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateZone = (zone, field, value) => setNgZones((zs) => zs.map((z) => z.zone === zone ? { ...z, [field]: value } : z));
  const saveNgZones = async () => {
    setSavingNg(true);
    try {
      const payload = { zones: ngZones.map((z) => ({ zone: z.zone, fee: parseFloat(z.fee) || 0, eta_days: parseInt(z.eta_days, 10) || 1 })) };
      const { data: r } = await api.put("/admin/nigeria-zones", payload);
      setNgZones(r.zones || []); toast.success("Nigeria delivery zones saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save zones"); }
    finally { setSavingNg(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data: s } = await api.put("/admin/finance/settings", { commission_rate: parseFloat(rate), trial_months: parseInt(trial, 10) });
      setSettings(s); toast.success("Finance settings saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (!data || !settings) return <AdminSectionSkeleton />;

  const kpis = [
    { icon: TrendingUp, label: "GMV (paid)", value: eur(data.gmv), color: "text-foreground" },
    { icon: Wallet, label: "Platform commission", value: eur(data.total_commission), color: "text-green-600", highlight: true },
    { icon: Banknote, label: "Net paid to sellers", value: eur(data.net_paid_sellers), color: "text-primary" },
    { icon: Users, label: "Pending withdrawals", value: eur(data.pending_withdrawals), color: "text-amber-600" },
  ];

  const txCols = [
    { header: "Date", accessor: (t) => new Date(t.created_at).toLocaleDateString() },
    { header: "Order", accessor: (t) => `#${(t.order_id || "").slice(-8)}` },
    { header: "Seller", accessor: (t) => t.seller_name },
    { header: "Gross (EUR)", accessor: (t) => t.gross.toFixed(2) },
    { header: "Commission (EUR)", accessor: (t) => t.commission.toFixed(2) },
    { header: "Rate", accessor: (t) => (t.commission_rate != null ? `${(t.commission_rate * 100).toFixed(1)}%` : "—") },
    { header: "Seller net (EUR)", accessor: (t) => t.credit.toFixed(2) },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className={`p-4 shadow-sm ${k.highlight ? "bg-primary text-primary-foreground" : ""}`} data-testid={`fin-kpi-${k.label}`}>
            <k.icon className={`w-5 h-5 mb-2 ${k.highlight ? "opacity-90" : k.color}`} />
            <p className="text-xl font-black font-display">{k.value}</p>
            <p className={`text-xs ${k.highlight ? "opacity-90" : "text-muted-foreground"}`}>{k.label}</p>
          </Card>
        ))}
      </div>

      <Card className="p-5 shadow-sm">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />Revenue Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data.series} data-testid="revenue-trends-chart">
            <defs>
              <linearGradient id="comm" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.5} /><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} /><stop offset="95%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip />
            <Area type="monotone" dataKey="revenue" name="Sales" stroke="#f59e0b" fill="url(#rev)" strokeWidth={2} />
            <Area type="monotone" dataKey="commission" name="Commission" stroke="hsl(var(--primary))" fill="url(#comm)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-5 shadow-sm" data-testid="finance-settings-card">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><Percent className="w-4 h-4 text-primary" />Commission & trial settings</h3>
        <p className="text-sm text-muted-foreground mb-4">Applied to future sales. Sellers pay 0% during their free trial, then this rate.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div><Label className="text-xs">Platform commission (%)</Label><Input data-testid="commission-rate-input" type="number" step="0.1" className="w-32 mt-1" value={rate} onChange={(e) => setRate(e.target.value)} /></div>
          <div><Label className="text-xs">Free trial (months)</Label><Input data-testid="trial-months-input" type="number" className="w-32 mt-1" value={trial} onChange={(e) => setTrial(e.target.value)} /></div>
          <Button data-testid="save-finance-settings" onClick={saveSettings} disabled={saving} className="rounded-full">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save settings"}</Button>
        </div>
      </Card>

      <Card className="p-5 shadow-sm" data-testid="nigeria-zones-card">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><Percent className="w-4 h-4 text-primary" />Nigeria delivery zones</h3>
        <p className="text-sm text-muted-foreground mb-4">Flat delivery fee (€) and estimated days per Nigerian geopolitical zone. Applied to future Nigeria orders.</p>
        <div className="space-y-3">
          {ngZones.map((z) => (
            <div key={z.zone} className="flex flex-wrap items-end gap-3 border-b border-border/40 pb-3" data-testid={`ng-zone-${z.zone.replace(/\s+/g, "-")}`}>
              <div className="min-w-[160px]">
                <p className="text-sm font-medium">{z.zone}</p>
                <p className="text-[11px] text-muted-foreground line-clamp-1">{z.states.join(", ")}</p>
              </div>
              <div><Label className="text-xs">Fee (€)</Label><Input data-testid={`ng-fee-${z.zone.replace(/\s+/g, "-")}`} type="number" step="0.5" className="w-24 mt-1" value={z.fee} onChange={(e) => updateZone(z.zone, "fee", e.target.value)} /></div>
              <div><Label className="text-xs">ETA (days)</Label><Input data-testid={`ng-eta-${z.zone.replace(/\s+/g, "-")}`} type="number" className="w-24 mt-1" value={z.eta_days} onChange={(e) => updateZone(z.zone, "eta_days", e.target.value)} /></div>
            </div>
          ))}
          <Button data-testid="save-nigeria-zones" onClick={saveNgZones} disabled={savingNg} className="rounded-full">{savingNg ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save zones"}</Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden shadow-sm">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-sm">Transactions <span className="text-muted-foreground font-normal">({data.transaction_count})</span></h3>
          <ExportMenu filename="transactions" title="Transactions" columns={txCols} rows={data.transactions} testid="transactions-export" />
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Order</TableHead><TableHead>Seller</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Commission</TableHead><TableHead className="text-center">Rate</TableHead><TableHead className="text-right">Seller net</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.transactions.map((t) => (
                <TableRow key={t.ledger_id} data-testid={`txn-row-${t.ledger_id}`}>
                  <TableCell className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="font-mono text-xs">#{(t.order_id || "").slice(-8)}</TableCell>
                  <TableCell className="text-sm">{t.seller_name}</TableCell>
                  <TableCell className="text-right text-sm">{eur(t.gross)}</TableCell>
                  <TableCell className="text-right text-sm font-medium text-green-600">{eur(t.commission)}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="text-[10px]">{t.commission_rate != null ? `${(t.commission_rate * 100).toFixed(1)}%` : "—"}</Badge></TableCell>
                  <TableCell className="text-right text-sm">{eur(t.credit)}</TableCell>
                </TableRow>
              ))}
              {data.transactions.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm">No transactions yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};
